import { useApolloClient } from '@apollo/client';
import { Button, message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UpdateVariantConfiguratorDocument,
    type UpdateVariantConfiguratorMutation,
    type UpdateVariantConfiguratorMutationVariables,
} from '../../../../api/mutations/updateVariantConfigurator';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import useHandleError from '../../../../utilities/useHandleError';
import { type VariantConfiguratorDetailsPageProps } from '../utils';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import VariantConfiguratorLayout from '../VariantConfiguratorLayout';
import useDetailsValidator from './useDetailsValidator';
import VariantConfiguratorDetailsForm from './VariantConfiguratorDetailsForm';

type VariantConfiguratorFormValue = {
    vehiclePrice: number;
};

const DetailsTab = ({ activeTab, replaceTab, disabled = false }: VariantConfiguratorDetailsPageProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const { variantConfigurator } = useVariantConfigurator();

    const apolloClient = useApolloClient();
    const initialValues: VariantConfiguratorFormValue = useMemo(
        () => ({ vehiclePrice: variantConfigurator.vehiclePrice || 0 }),
        [variantConfigurator.vehiclePrice]
    );

    const validator = useDetailsValidator();

    const onSubmit = useHandleError<VariantConfiguratorFormValue>(
        async value => {
            message.loading({
                content: t('variantConfiguratorDetails:messages.details.saveSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient.mutate<UpdateVariantConfiguratorMutation, UpdateVariantConfiguratorMutationVariables>({
                mutation: UpdateVariantConfiguratorDocument,
                variables: {
                    variantConfiguratorId: variantConfigurator.id,
                    vehiclePrice: value.vehiclePrice,
                },
            });

            message.success({
                content: t('variantConfiguratorDetails:messages.details.saveSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, t, variantConfigurator.id]
    );

    return (
        <VariantConfiguratorLayout
            activeTab={activeTab}
            footer={[
                !disabled && (
                    <Button key="save" form="variantConfiguratorDetailsForm" htmlType="submit" type="primary">
                        {t('variantConfiguratorDetails:actions.options.save')}
                    </Button>
                ),
            ]}
            replaceTab={replaceTab}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
                {({ handleSubmit }) => (
                    <Form
                        id="variantConfiguratorDetailsForm"
                        name="variantConfiguratorDetailsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <FormAutoTouch />
                        <VariantConfiguratorDetailsForm disabled={disabled} />
                    </Form>
                )}
            </Formik>
        </VariantConfiguratorLayout>
    );
};

export default DetailsTab;
